import { useEffect, useMemo, useState } from "react";
import type { ListingFacetCategoryData } from "src/types/adapters/listing.type";
import { getSubcategories } from "src/utils/category-tree-helpers";
import { ShopType } from "src/utils/genderLocale/genderLocale";
import useProductFiltersOptionOrder from "../useProductFiltersOptionOrder";
import { useShopTypeContext } from "../context/use-shop-type-context";
import { useSearchProvider } from "../use-search-provider";

type ParentCategory = {
  id: string;
  slug: string;
};

type FacetState = {
  loading: boolean;
  categories: ListingFacetCategoryData[];
};

// The category facet only lists categories that have products, so empty subcategories never show.
function useSubcategories(category?: ParentCategory) {
  const { currentShopType } = useShopTypeContext();
  const [searchProvider, isReady] = useSearchProvider();
  const { data: orderData, loading: orderLoading } =
    useProductFiltersOptionOrder();

  const [state, setState] = useState<FacetState>({
    loading: false,
    categories: [],
  });

  const gender = currentShopType === ShopType.All ? undefined : currentShopType;
  const { id, slug } = category ?? {};

  useEffect(() => {
    if (!isReady || !searchProvider || !id || !slug) {
      return;
    }

    let ignore = false;
    setState({ loading: true, categories: [] });

    void searchProvider
      .browse<"facets">({
        entity: "facets",
        facetName: "category",
        facetValue: slug,
        parameters: {
          hitsPerPage: 1,
          filters: gender ? { genders: [gender] } : undefined,
        },
      })
      .then(({ results }) => {
        if (ignore) {
          return;
        }

        const facet = results.get("category");

        setState({
          loading: false,
          categories: facet?.type === "category" ? facet.data : [],
        });
      });

    return () => {
      ignore = true;
    };
  }, [searchProvider, isReady, id, slug, gender]);

  const subcategories = useMemo(
    () =>
      id
        ? getSubcategories(state.categories, id, {
            gender,
            sortOrder: orderData?.productFiltersOptionOrder?.category,
          })
        : [],
    [state.categories, id, gender, orderData]
  );

  return {
    subcategories,
    loading: state.loading || (orderLoading && !orderData),
  };
}

export { useSubcategories };
